import { useEffect, useMemo, useRef, useState } from 'react';
import { Layer, Source } from 'react-map-gl/maplibre';
import type { FillLayerSpecification, LineLayerSpecification } from 'maplibre-gl';
import { makeCirclePolygon } from '@/lib/geo';
import { getRadiusKm } from '@/data/leveling';
import { useGameStore } from '@/state/gameStore';

const GROW_DURATION_MS = 1500;

const FILL_LAYER: FillLayerSpecification = {
  id: 'radius-fill',
  type: 'fill',
  source: 'radius',
  paint: {
    'fill-color': '#2563eb',
    'fill-opacity': 0.08,
  },
};

const LINE_LAYER: LineLayerSpecification = {
  id: 'radius-line',
  type: 'line',
  source: 'radius',
  paint: {
    'line-color': '#2563eb',
    'line-opacity': 0.4,
    'line-width': 1.5,
  },
};

/**
 * Blue translucent circle centred on the player's home city, sized by level.
 * Animates radius over GROW_DURATION_MS when the level changes.
 */
export function RadiusCircle() {
  const homeCity = useGameStore((s) => s.homeCity);
  const level = useGameStore((s) => s.level);
  const visible = useGameStore((s) => s.uiPrefs.layerToggles.radius);
  const targetRadiusKm = useMemo(() => getRadiusKm(level), [level]);

  const [displayRadiusKm, setDisplayRadiusKm] = useState(targetRadiusKm);
  const fromRadiusRef = useRef(targetRadiusKm);
  const animStartRef = useRef<number | null>(null);

  useEffect(() => {
    if (targetRadiusKm === displayRadiusKm) return;
    fromRadiusRef.current = displayRadiusKm;
    animStartRef.current = performance.now();

    let frame = 0;
    const step = (now: number) => {
      const start = animStartRef.current ?? now;
      const t = Math.min(1, (now - start) / GROW_DURATION_MS);
      // easeOutCubic
      const eased = 1 - Math.pow(1 - t, 3);
      const value = fromRadiusRef.current + (targetRadiusKm - fromRadiusRef.current) * eased;
      setDisplayRadiusKm(value);
      if (t < 1) frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetRadiusKm]);

  const geojson = useMemo(() => {
    if (!homeCity) return null;
    const ring = makeCirclePolygon(homeCity.center, displayRadiusKm);
    return {
      type: 'FeatureCollection' as const,
      features: [
        {
          type: 'Feature' as const,
          properties: {},
          geometry: { type: 'Polygon' as const, coordinates: [ring] },
        },
      ],
    };
  }, [homeCity, displayRadiusKm]);

  if (!homeCity || !geojson || !visible) return null;

  return (
    <Source id="radius" type="geojson" data={geojson}>
      <Layer {...FILL_LAYER} />
      <Layer {...LINE_LAYER} />
    </Source>
  );
}
